export type TenantIdSource = {
  tenantId: string | null;
  hostname: string | null;
  reason: string;
};

function stripPort(host: string) {
  return host.replace(/:\d+$/, "");
}

export function sanitizeTenantId(raw: string): string | null {
  const s = String(raw || "")
    .trim()
    .toLowerCase();
  if (!s) return null;

  const cleaned = s
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

  if (!cleaned) return null;
  if (cleaned.length > 60) return cleaned.slice(0, 60);
  return cleaned;
}

export function resolveTenantIdFromHostname(
  hostnameRaw: string | null | undefined,
  opts?: { defaultTenantId?: string | null },
): TenantIdSource {
  const defaultTenantId = sanitizeTenantId(opts?.defaultTenantId || "") || null;
  const hostname = stripPort(String(hostnameRaw || "").trim().toLowerCase()) || null;
  if (!hostname) return { tenantId: defaultTenantId, hostname: null, reason: "missing_hostname" };

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return { tenantId: defaultTenantId, hostname, reason: "localhost" };
  }

  // Local dev support: treat *.localhost as a valid subdomain tenant.
  // Browsers resolve foo.localhost to 127.0.0.1 in many environments.
  if (hostname.endsWith(".localhost")) {
    const sub = hostname.slice(0, -".localhost".length).split(".")[0] || "";
    const tenantId = sanitizeTenantId(sub);
    return { tenantId: tenantId || defaultTenantId, hostname, reason: tenantId ? "subdomain_localhost" : "invalid_subdomain" };
  }

  const parts = hostname.split(".").filter(Boolean);
  if (parts.length < 3) {
    // e.g. peopleos.app or custom apex domain
    return { tenantId: defaultTenantId, hostname, reason: "no_subdomain" };
  }

  const sub = parts[0] || "";
  const tenantId = sanitizeTenantId(sub);
  return { tenantId: tenantId || defaultTenantId, hostname, reason: tenantId ? "subdomain" : "invalid_subdomain" };
}

export function resolveTenantIdFromBrowser(opts?: { defaultTenantId?: string | null }): TenantIdSource {
  if (typeof window === "undefined") {
    return { tenantId: sanitizeTenantId(opts?.defaultTenantId || "") || null, hostname: null, reason: "no_window" };
  }
  return resolveTenantIdFromHostname(window.location?.hostname || "", opts);
}
